import Button from 'ext-ui/components/Button/Button'
import Dropdown from 'ext-ui/components/Dropdown/Dropdown'
import MenuOptionTertiary from 'ext-ui/components/MenuOptionTertiary/MenuOptionTertiary'
import Overlay from 'ext-ui/components/Overlay/Overlay'
import React, { useCallback, useEffect, useId, useRef, useState } from 'react'
import { useClickOutside } from 'src/hooks'
import { useTranslate } from 'src/localization/Trns'
import type { StatementDocumentFormat } from 'src/page-components/Accounts/AccountStatementsTab/accountStatementsDemoData'
import * as style from 'src/page-components/Accounts/AccountStatementsTab/components/StatementExportMenu/StatementExportMenu.style'
import { useStatementDocumentFormatLabels } from 'src/page-components/Accounts/AccountStatementsTab/statementDocumentFormatLabels'

interface StatementExportMenuProps {
  /**
   * The formats this statement can actually be downloaded in, from the wire's
   * `availableDocumentFormats`. The menu renders nothing when empty — an
   * unavailable download is better absent than present and dead.
   */
  availableFormats: StatementDocumentFormat[]
  /** Period label, for the trigger's accessible name. */
  period: string
  /** True while a download is in flight; the trigger stays disabled. */
  isDownloading: boolean
  onSelect: (format: StatementDocumentFormat) => void
}

/**
 * The per-row statement Download menu (EX-40602).
 *
 * Presentational by design: it is handed the formats to offer and reports the
 * one picked. Fetching, generating, and delivering the file all live in
 * `useStatementDownload`, so this component holds no data access and can be
 * rendered in a test with four props.
 *
 * ## Menu semantics are the component's real work
 *
 * `Dropdown` renders `role="menu"` and `MenuOptionTertiary` renders
 * `role="menuitem"`, and that pairing carries an obligation: a menu is expected
 * to place focus on an item when it opens, move focus with the arrow keys, and
 * return focus to its trigger when it closes. Tab, Escape, and an outside click
 * all close it. Without that, the role tells a screen-reader user to press a
 * key that does nothing.
 *
 * Options are located by querying `[role="menuitem"]` inside the menu rather
 * than by collecting refs: `MenuOptionTertiary` is a generated function
 * component and does not forward one. The query reads the role the DS component
 * itself sets, so it fails loudly (no focus movement) rather than silently
 * targeting the wrong element if that ever changes.
 */
const StatementExportMenu: React.FC<StatementExportMenuProps> = ({
  availableFormats,
  period,
  isDownloading,
  onSelect,
}) => {
  const translate = useTranslate()
  // Shared with the download failure copy, so the sentence after a rejected
  // download names the same thing the user clicked.
  const formatLabel = useStatementDocumentFormatLabels()
  const [isOpen, setIsOpen] = useState(false)
  const triggerRef = useRef<HTMLButtonElement>(null)
  const menuRef = useRef<HTMLDivElement>(null)
  const menuId = useId()

  const close = useCallback(() => setIsOpen(false), [])

  /**
   * Closing returns focus to the trigger — the option that had focus is about
   * to unmount, and focus left on a removed node falls back to `<body>`,
   * stranding a keyboard user at the top of the page.
   */
  const closeAndRestoreFocus = useCallback(() => {
    setIsOpen(false)
    triggerRef.current?.focus()
  }, [])

  /**
   * Closing because the user clicked elsewhere.
   *
   * Clicking non-focusable whitespace strands focus: the option holding it is
   * unmounted and the click itself moves focus nowhere, so it lands on
   * `<body>` — the same stranding `closeAndRestoreFocus` prevents on every
   * other close path.
   *
   * The re-anchor is DEFERRED rather than done inline, and it has to be.
   * `useClickOutside` fires on `mousedown`, ahead of the browser's own focus
   * handling, so focusing the trigger here is simply undone a moment later when
   * the click blurs to `<body>`. Running after the event settles also makes the
   * check honest: by then `activeElement` distinguishes "the click took focus
   * somewhere real" from "focus fell off the page", and only the second is ours
   * to correct — the first must be left alone or we would yank focus out of
   * whatever the user actually clicked.
   */
  const restoreFocusTimer = useRef<ReturnType<typeof setTimeout>>()

  const closeFromOutsideClick = useCallback(() => {
    const hadFocusInside = menuRef.current?.contains(document.activeElement)
    setIsOpen(false)

    if (!hadFocusInside) {
      return
    }

    restoreFocusTimer.current = setTimeout(() => {
      // `body` (or nothing) means the click focused nothing of its own.
      if (
        document.activeElement === null ||
        document.activeElement === document.body
      ) {
        triggerRef.current?.focus()
      }
    })
  }, [])

  // The timer outlives a menu closed by an outside click, so an unmount between
  // the click and the callback would otherwise fire it against a dead ref.
  useEffect(
    () => () => {
      if (restoreFocusTimer.current) {
        clearTimeout(restoreFocusTimer.current)
      }
    },
    [],
  )

  const anchorRef = useClickOutside(closeFromOutsideClick)

  const menuOptions = (): HTMLElement[] =>
    Array.from(
      menuRef.current?.querySelectorAll<HTMLElement>('[role="menuitem"]') ?? [],
    )

  // Focus the first option on open, which is what makes the arrow keys below
  // reachable at all: without it focus stays on the trigger and the menu's key
  // handler never sees a keystroke.
  useEffect(() => {
    if (isOpen) {
      menuOptions()[0]?.focus()
    }
  }, [isOpen])

  const onMenuKeyDown = (event: React.KeyboardEvent<HTMLDivElement>): void => {
    if (event.key === 'Escape') {
      // Stopped here so Escape closes the menu WITHOUT also reaching the
      // statement modal or any dialog this row is rendered inside.
      event.stopPropagation()
      closeAndRestoreFocus()
      return
    }

    /*
     * Tab out of a menu closes it, per the menu pattern.
     *
     * Focus moves to the TRIGGER first and the default Tab action is then left
     * to run: the browser resolves "next focusable" from wherever focus sits
     * once the handler returns, so closing without this leaves it resolving
     * from an option that React has already unmounted — and Tab restarts from
     * the top of the document instead of continuing past the trigger.
     */
    if (event.key === 'Tab') {
      triggerRef.current?.focus()
      close()
      return
    }

    if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') {
      return
    }

    event.preventDefault()
    const options = menuOptions()
    if (options.length === 0) {
      return
    }

    const focusedIndex = options.indexOf(document.activeElement as HTMLElement)
    const step = event.key === 'ArrowDown' ? 1 : -1
    // Wraps at both ends, so ArrowUp from the first option lands on the last.
    const nextIndex = (focusedIndex + step + options.length) % options.length

    options[nextIndex]?.focus()
  }

  // Nothing to offer, nothing to render. A trigger that opens an empty menu is
  // a worse answer than no trigger: the contract publishes an empty list when a
  // statement genuinely has no document yet.
  if (availableFormats.length === 0) {
    return null
  }

  /** The chip glyph. Decorative — it repeats the label beside it. */
  const formatChipLabel: Record<StatementDocumentFormat, string> = {
    PDF: 'PDF',
    XLSX: 'XLS',
    CSV: 'CSV',
  }

  return (
    <div css={style.anchor} ref={anchorRef}>
      <Button
        ref={triggerRef}
        variant="text"
        /*
         * `aria-disabled`, NOT `disabled`, while a download is in flight.
         *
         * Selecting a format returns focus to this trigger and starts the
         * download in the same tick, so the very next render would disable the
         * element that just took focus — and a browser blurs a focused element
         * the moment it becomes disabled, dropping the user to `<body>` for the
         * length of the request. `aria-disabled` announces the busy state and
         * keeps the control focusable; the click guard below is what actually
         * refuses the second activation.
         */
        aria-disabled={isDownloading}
        aria-busy={isDownloading}
        aria-haspopup="menu"
        aria-expanded={isOpen}
        aria-controls={isOpen ? menuId : undefined}
        // Every row carries an identical "Download", so each needs a distinct
        // accessible name to be usable from a screen reader's element list.
        aria-label={translate('Download ${period} statement', { period })}
        onClick={() => {
          if (isDownloading) {
            return
          }
          setIsOpen((wasOpen) => !wasOpen)
        }}
      >
        {isDownloading ? translate('Downloading…') : translate('Download')}
      </Button>
      {isOpen && (
        <div css={style.menu} ref={menuRef}>
          {/*
            `Overlay` is not decoration — `Dropdown` declares it as a
            composition dependency and ships NO chrome of its own (its generated
            style is width and max-height, nothing else). Overlay is where the
            surface background, border, 8px radius, shadow, and `overflow: clip`
            come from, and that last one matters structurally: the DS pins
            `MenuOptionTertiary` at 329px inside a 320px `Dropdown`, so without
            a clipping ancestor every option juts 9px past the menu's edge. The
            design render composes the same two and clips the same overflow.
          */}
          <Overlay padding="none">
            <Dropdown
              id={menuId}
              isFooterAbsolute={false}
              aria-label={translate('Download ${period} statement as', {
                period,
              })}
              onKeyDown={onMenuKeyDown}
            >
              {availableFormats.map((format) => (
                <MenuOptionTertiary
                  key={format}
                  title={formatLabel[format]}
                  icon={
                    <span css={style.formatChip} aria-hidden="true">
                      {formatChipLabel[format]}
                    </span>
                  }
                  onClick={() => {
                    closeAndRestoreFocus()
                    onSelect(format)
                  }}
                />
              ))}
            </Dropdown>
          </Overlay>
        </div>
      )}
    </div>
  )
}

export default StatementExportMenu
